import React from "react";
import { Modal, Input, DatePicker, TimePicker, Select, Checkbox } from "antd";
import dayjs from "dayjs";
import { DynamicField } from "./types";
import {
    isDropdownType,
    isDateType,
    isTimeType,
    isCheckboxType,
    isBoolean,
    FieldType,
    parseToIso,
    parseMsDate,
} from "./dynamicFieldUtils";

interface LineItemsModalProps {
    open: boolean;
    onClose: () => void;
    currentLineItem: any;
    setCurrentLineItem: React.Dispatch<React.SetStateAction<any>>;
    editingLineItemIndex: number | null;
    dynamicConfig: any;
    onSave: () => void;
    availableDependents?: any[];
    reimbursementType?: string;
}

export const LineItemsModal: React.FC<LineItemsModalProps> = ({
    open,
    onClose,
    currentLineItem,
    setCurrentLineItem,
    editingLineItemIndex,
    dynamicConfig,
    onSave,
    availableDependents = [],
    reimbursementType = "",
}) => {
    const tableHeaders = dynamicConfig?.configData?.parsedMultilineTableHeaders || dynamicConfig?.parsedMultilineTableHeaders || [];
    const visibleHeaders = tableHeaders
        .filter(
            (header: DynamicField) =>
                isBoolean(header.VisibleToEmployee)
        )
        .sort((a: DynamicField, b: DynamicField) => {
            const orderA = parseInt(a.FieldsOrder || "0", 10);
            const orderB = parseInt(b.FieldsOrder || "0", 10);
            if (orderA !== orderB) return orderA - orderB;

            // Secondary sort: ensure LINUM (Line No) comes first
            const isLinumA = (a.LineCustomerField || a.HeaderCustomerField) === "LINUM";
            const isLinumB = (b.LineCustomerField || b.HeaderCustomerField) === "LINUM";
            if (isLinumA && !isLinumB) return -1;
            if (!isLinumA && isLinumB) return 1;
            return 0;
        });

    const renderLineItemField = (header: DynamicField, index: number) => {
        const fieldName = (header.LineCustomerField || header.HeaderCustomerField || "") as string;
        let fieldLabel = header.Label || fieldName;
        if (fieldLabel.endsWith("*")) {
            fieldLabel = fieldLabel.slice(0, -1).trim();
        }
        const isRequired = isBoolean(header.CustFieldMandatory) || isBoolean(header.CustFldMandatory);

        // Disable empanelled hospital (C40T2) until hospital status (C10T3) is selected (only for Medical where C10T3 header exists)
        const hasC10T3Header = visibleHeaders.some(
            (h: DynamicField) => (h.LineCustomerField || h.HeaderCustomerField) === "C10T3"
        );
        const isDisabledUntilC10T3 = fieldName === "C40T2" && hasC10T3Header && !currentLineItem?.C10T3;

        const isReadOnly =
            isDisabledUntilC10T3 ||
            isBoolean(header.CustFldReadOnly) ||
            isBoolean(header.ReadOnlyForEmployee) ||
            isBoolean(header.ReadOnlyForEmp);

        const type = (header.ViewOfFieldType ?? "") as FieldType;
        const currentValue = currentLineItem?.[fieldName] ?? "";

        const handleFieldChange = (val: any) => {
            if (fieldName === "FAOBJ") {
                // Dependent change auto-fills sibling read-only dependent details
                const dep = (availableDependents || []).find((d: any) => d.FamsaObjps === val);
                let birthDate = dep?.BirthDate ?? "";
                if (reimbursementType === "ZLTA" && birthDate) {
                    const [day, month, year] = String(birthDate).split(".");
                    const ms = day && month && year ? Date.UTC(Number(year), Number(month) - 1, Number(day)) : NaN;
                    if (!isNaN(ms)) birthDate = `/Date(${ms})/`;
                }
                setCurrentLineItem((prev: any) => ({
                    ...(prev || {}),
                    FAOBJ: val,
                    FAMSA: dep?.FamilyMember ?? "",
                    FAMTX: dep?.Name ?? "",
                    FGBDT: birthDate,
                    FASEX: dep?.Gender ?? "",
                    FCNAM: dep?.FullName ?? "",
                    AGE_Y: dep?.AgeYears ?? "",
                }));
                return;
            }

            if (fieldName === "C10T3") {
                // Selecting hospital status resets empanelled hospital selection
                setCurrentLineItem((prev: any) => ({
                    ...(prev || {}),
                    C10T3: val,
                    C40T2: "",
                }));
                return;
            }

            setCurrentLineItem((prev: any) => ({
                ...prev,
                [fieldName]: val,
            }));
        };

        // Extract dropdown options safely
        let dropdownOptions: any[] = [];
        if (fieldName === "FAOBJ") {
            dropdownOptions = (availableDependents || []).map((dep: any) => ({
                value: dep.FamsaObjps,
                label: dep.FamilyMemberText || `${dep.Name || ""}: ${dep.FullName || ""}`.trim(),
            }));
        } else {
            const rawOptions = header.OptionsValue?.results || header.DropdownValues || [];
            dropdownOptions = rawOptions.map((opt: any) => ({
                value: opt.SelectedValue ?? opt.value,
                label: opt.SelectedText ?? opt.label ?? opt.SelectedValue,
            }));
        }

        if (fieldName === "C40T2" && hasC10T3Header) {
            dropdownOptions.unshift({
                value: "",
                label: "Non-Nominated/Govt./Outstation Hospital",
            });
        }

        // Special value display formatting for FGBDT
        let displayValue = currentValue;
        if (fieldName === "FGBDT" && currentValue) {
            const parsed = parseMsDate(currentValue);
            if (parsed) {
                displayValue = parsed.replace(/-/g, ".");
            }
        }

        return (
            <div key={index} className="col-md-6 mb-3">
                <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: "13px" }}>
                    {fieldLabel} {isRequired && <span className="text-danger">*</span>}
                </label>

                {isCheckboxType(type) ? (
                    <div className="pt-2">
                        <Checkbox
                            checked={currentValue === true || currentValue === "true" || currentValue === "YES"}
                            onChange={(e) => handleFieldChange(e.target.checked ? "YES" : "")}
                            disabled={isReadOnly}
                        >
                            {fieldLabel}
                        </Checkbox>
                    </div>
                ) : isDropdownType(type) ? (
                    <Select
                        showSearch
                        allowClear
                        placeholder={`Select ${fieldLabel}`}
                        className="w-100"
                        style={{ height: "40px" }}
                        value={currentValue || undefined}
                        onChange={handleFieldChange}
                        disabled={isReadOnly}
                        options={dropdownOptions}
                        optionFilterProp="label"
                    />
                ) : isTimeType(type, fieldName, fieldLabel) ? (
                    <TimePicker
                        className="w-100"
                        style={{ height: "40px" }}
                        format="HH:mm"
                        placement="bottomLeft"
                        popupStyle={{ zIndex: 9999 }}
                        value={currentValue ? dayjs(currentValue, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null}
                        onChange={(time) => {
                            handleFieldChange(time ? time.format("HH:mm:ss") : "");
                        }}
                        disabled={isReadOnly}
                        placeholder={`Select ${fieldLabel}`}
                    />
                ) : isDateType(type, fieldName, fieldLabel) ? (
                    <DatePicker
                        className="w-100"
                        style={{ height: "40px" }}
                        format="DD-MM-YYYY"
                        placement="bottomLeft"
                        popupStyle={{ zIndex: 9999 }}
                        value={currentValue ? dayjs(parseToIso(currentValue), "YYYY-MM-DD") : null}
                        onChange={(date) => {
                            if (date) {
                                const ms = Date.UTC(date.year(), date.month(), date.date());
                                handleFieldChange(`/Date(${ms})/`);
                            } else {
                                handleFieldChange("");
                            }
                        }}
                        disabled={isReadOnly}
                        placeholder={`Select ${fieldLabel}`}
                    />
                ) : (
                    (() => {
                        const widthOnESSScreen = parseInt(header.WidthOnESSScreen || "0", 10);
                        const isAssetField = fieldLabel.toLowerCase().includes("asset") || fieldName.toLowerCase().includes("asset");
                        const maxLength = widthOnESSScreen > 0 ? widthOnESSScreen : (isAssetField ? 16 : undefined);

                        return (
                            <Input
                                placeholder={`Enter ${fieldLabel}`}
                                style={{ height: "40px" }}
                                value={displayValue}
                                maxLength={maxLength}
                                onChange={(e) => {
                                    let val = e.target.value;
                                    if (isAssetField) {
                                        const max = maxLength || 16;
                                        if (val.length > max) val = val.slice(0, max);
                                    }
                                    if (maxLength && val.length > maxLength) {
                                        val = val.slice(0, maxLength);
                                    }
                                    handleFieldChange(val);
                                }}
                                disabled={isReadOnly}
                            />
                        );
                    })()
                )}
            </div>
        );
    };

    return (
        <Modal
            title={
                <span className="fw-bold text-dark fs-16">
                    <i className="ti ti-list-details me-2 text-primary"></i>
                    {editingLineItemIndex !== null ? "Edit Line Item" : "Add Line Item"}
                </span>
            }
            open={open}
            onCancel={onClose}
            onOk={onSave}
            okText={editingLineItemIndex !== null ? "Update Item" : "Add Item"}
            cancelText="Cancel"
            width={700}
            style={{ top: 50 }}
            okButtonProps={{ disabled: visibleHeaders.length === 0 }}
        >
            <div className="py-2" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                {visibleHeaders.length > 0 ? (
                    <div className="row g-3">
                        {visibleHeaders.map((header: DynamicField, idx: number) =>
                            renderLineItemField(header, idx)
                        )}
                    </div>
                ) : (
                    <div className="alert alert-info mb-0">
                        <i className="ti ti-info-circle me-2"></i>
                        No line item fields are configured for this request type.
                    </div>
                )}
            </div>
            <style>{`
                .ant-picker-dropdown {
                    z-index: 9999 !important;
                }
                .ant-picker-header {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                }
                .ant-picker-header-view {
                    display: flex !important;
                    align-items: center !important;
                    gap: 6px !important;
                }
                .ant-picker-header-view button {
                    font-weight: 600 !important;
                    color: #1f2937 !important;
                    cursor: pointer !important;
                }
                .ant-picker-header-view button:hover {
                    color: #ab1f24 !important;
                }
                .ant-picker-header-super-prev-btn,
                .ant-picker-header-prev-btn,
                .ant-picker-header-next-btn,
                .ant-picker-header-super-next-btn {
                    visibility: visible !important;
                    opacity: 1 !important;
                    cursor: pointer !important;
                }
                .ant-picker-header-super-prev-btn::before,
                .ant-picker-header-prev-btn::before,
                .ant-picker-header-next-btn::before,
                .ant-picker-header-super-next-btn::before {
                    content: none !important;
                }
            `}</style>
        </Modal>
    );
};
